import * as turf from "@turf/turf";
import { Feature, FeatureCollection, Geometry } from "geojson";
import { ProductMeasurementJob, ProposalMeasurementType } from "generated/graphql";
import { meterConversions } from "constants/meterConversions";

export type ExistingStageMeasurement = {
  geojsonFeatureId?: string | null;
  measurementJob?: ProductMeasurementJob | null;
  measurementType?: ProposalMeasurementType | null;
  measurementValue?: number | null;
  featureGeojson?: string | null;
  label?: string | null;
  sortOrder?: number | null;
  productId?: string | null;
  isScreenshotTarget?: boolean | null;
  product?: {
    id: string;
    name: string;
    measurementJob?: ProductMeasurementJob | null;
    dailyUnitsComplete?: string | number | null;
  } | null;
} | null;

type MeasurementMetrics = {
  areaFeet: number;
  areaYards: number;
  perimeterFeet: number;
  perimeterYards: number;
};

export type StageMeasurementDraft = {
  featureId: string;
  label: string;
  sortOrder: number;
  measurementType: ProposalMeasurementType;
  featureGeojson: string;
  metrics: MeasurementMetrics;
  productId: string | null;
  measurementJob: ProductMeasurementJob | null;
  measurementValue: number | null;
  isScreenshotTarget: boolean;
  productName?: string | null;
  defaultDailyUnitsComplete?: string | number | null;
  color?: string | null;
};

const createFeatureId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `feature-${Date.now()}-${Math.round(Math.random() * 100000)}`;

export const normalizeStageMapData = (
  featureCollection: FeatureCollection | null | undefined
): FeatureCollection | null => {
  if (!featureCollection) {
    return null;
  }

  return {
    ...featureCollection,
    features: featureCollection.features.map((feature) => ({
      ...feature,
      properties: {
        ...(feature.properties ?? {}),
        featureId: feature.properties?.featureId ?? createFeatureId(),
      },
    })),
  };
};

const isCircleFeature = (feature: Feature<Geometry>) =>
  feature.geometry.type === "Point" && typeof feature.properties?.radius === "number";

const isAreaFeature = (feature: Feature<Geometry>) =>
  feature.geometry.type === "Polygon" ||
  feature.geometry.type === "MultiPolygon" ||
  isCircleFeature(feature);

const isPerimeterFeature = (feature: Feature<Geometry>) =>
  feature.geometry.type === "LineString" || feature.geometry.type === "MultiLineString";

export const isMeasurableFeature = (feature: Feature<Geometry>) =>
  isAreaFeature(feature) || isPerimeterFeature(feature);

const calculateCircleMetrics = (feature: Feature<Geometry>): MeasurementMetrics => {
  const radiusMeters = Number(feature.properties?.radius ?? 0);
  const radiusFeet = radiusMeters * meterConversions.feet;
  // meterConversions.yards is stored as meters-per-yard, so divide to get yards-per-meter.
  const radiusYards = radiusMeters / meterConversions.yards;

  return {
    areaFeet: Math.ceil(Math.PI * radiusFeet * radiusFeet),
    areaYards: Math.ceil(Math.PI * radiusYards * radiusYards),
    perimeterFeet: Math.round(2 * Math.PI * radiusFeet),
    perimeterYards: Math.round(2 * Math.PI * radiusYards),
  };
};

const calculateFeatureMetrics = (feature: Feature<Geometry>): MeasurementMetrics => {
  if (isCircleFeature(feature)) {
    return calculateCircleMetrics(feature);
  }

  if (isAreaFeature(feature)) {
    const areaMeters = turf.area(feature as any);
    const polygon = feature as any;
    const perimeterFeet = Math.round(turf.length(turf.polygonToLine(polygon), { units: "feet" }));
    const perimeterYards = Math.round(turf.length(turf.polygonToLine(polygon), { units: "yards" }));

    return {
      areaFeet: Math.ceil(areaMeters * meterConversions.squareFeet),
      areaYards: Math.ceil(areaMeters * meterConversions.squareYards),
      perimeterFeet,
      perimeterYards,
    };
  }

  return {
    areaFeet: 0,
    areaYards: 0,
    perimeterFeet: Math.round(turf.length(feature as any, { units: "feet" })),
    perimeterYards: Math.round(turf.length(feature as any, { units: "yards" })),
  };
};

export const getMeasurementTypeForFeature = (
  feature: Feature<Geometry>
): ProposalMeasurementType | null => {
  if (isAreaFeature(feature)) {
    return ProposalMeasurementType.AREA;
  }

  if (isPerimeterFeature(feature)) {
    return ProposalMeasurementType.PERIMETER;
  }

  return null;
};

export const getMeasurementValueForJob = (
  metrics: MeasurementMetrics,
  measurementJob?: ProductMeasurementJob | null
): number | null => {
  switch (measurementJob) {
    case ProductMeasurementJob.SQUARE_FOOT:
      return metrics.areaFeet;
    case ProductMeasurementJob.SQUARE_YARD:
      return metrics.areaYards;
    case ProductMeasurementJob.LINEAR_FOOT:
      return metrics.perimeterFeet;
    case ProductMeasurementJob.UNIT:
      return metrics.areaFeet;
    default:
      return null;
  }
};

export const isMeasurementCompatible = (
  measurementType: StageMeasurementDraft["measurementType"],
  measurementJob?: ProductMeasurementJob | null
) => {
  if (measurementType === ProposalMeasurementType.AREA) {
    return [
      ProductMeasurementJob.SQUARE_FOOT,
      ProductMeasurementJob.SQUARE_YARD,
      ProductMeasurementJob.UNIT,
    ].includes(measurementJob as ProductMeasurementJob);
  }

  return measurementJob === ProductMeasurementJob.LINEAR_FOOT;
};

export const buildStageMeasurementDrafts = (
  featureCollection: FeatureCollection | null | undefined,
  existingMeasurements: ExistingStageMeasurement[] = []
): StageMeasurementDraft[] => {
  if (!featureCollection) {
    return [];
  }

  const existingByFeatureId = new Map(
    existingMeasurements
      .filter(Boolean)
      .map((measurement) => [measurement?.geojsonFeatureId ?? "", measurement])
  );

  return featureCollection.features
    .filter((feature) => isMeasurableFeature(feature))
    .map((feature, index) => {
      const featureId = String(feature.properties?.featureId ?? createFeatureId());
      const existing = existingByFeatureId.get(featureId);
      const measurementType = getMeasurementTypeForFeature(feature);
      const metrics = calculateFeatureMetrics(feature);
      const measurementJob = existing?.measurementJob ?? existing?.product?.measurementJob ?? null;
      const label =
        feature.properties?.labelText ||
        existing?.label ||
        `${measurementType === ProposalMeasurementType.AREA ? "Area" : "Perimeter"} ${index + 1}`;

      return {
        featureId,
        label,
        sortOrder: index,
        measurementType: measurementType as ProposalMeasurementType,
        featureGeojson: JSON.stringify(feature),
        metrics,
        color: feature.properties?.color ?? null,
        productId: existing?.productId ?? null,
        measurementJob,
        measurementValue:
          getMeasurementValueForJob(metrics, measurementJob) ?? existing?.measurementValue ?? null,
        isScreenshotTarget: !!existing?.isScreenshotTarget,
        productName: existing?.product?.name ?? null,
        defaultDailyUnitsComplete: existing?.product?.dailyUnitsComplete ?? null,
      };
    });
};
